import styled, {
  IFalconTheme,
  Layout,
  ResponsiveTypography,
  ResponsiveTypographyVariant,
  Scale,
  StyledComponent,
  withTheme,
} from '@se-admin/falcon';
import * as React from 'react';
import { PriceValueVariant } from '../PriceValue/PriceValue.interfaces';
import { LineThrough } from './CostView.styles';

export interface ICostViewStyle {
  priceColor: string;
  textColor: string;
}
export interface ICostViewProps {
  /**
   * Translated prefix displayed just before leadRate section.
   */
  translatedPrefix: string;
  /**
   * Theme to be used in the components.
   */
  theme?: IFalconTheme;
  /**
   * Translated suffix displayed just after leadRate section.
   */
  translatedSuffix: string;
  /**
   * Lead rate.
   */
  leadRate: string;
  /**
   * Rack rate.
   */
  rackRate?: string;
  /**
   * Rack rate visibility.
   */
  hideRackRate?: boolean;
  /**
   * Optional styles aplied to component.
   */
  styles?: ICostViewStyle;
  /**
   * Price value variant where this component is in.
   */
  variant?: PriceValueVariant;
}

const CostContainer: StyledComponent<typeof Layout.Container, IFalconTheme, {}> = styled(
  Layout.Container
)`
  flex-wrap: wrap;
  margin-bottom: ${Scale.xxxs};
`;

const CostWrapper: StyledComponent<'span', {}, {}> = styled('span')`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
`;

const PaddedTypography: StyledComponent<typeof ResponsiveTypography, {}, {}> = styled(
  ResponsiveTypography
)`
  padding-right: 5px;
`;

const CostView: React.FunctionComponent<ICostViewProps> = ({
  translatedPrefix,
  theme,
  translatedSuffix,
  leadRate,
  rackRate,
  hideRackRate,
  styles,
  variant,
}) => {
  const priceColor: string = styles && styles.priceColor ? styles.priceColor : theme.color.primary;
  const textColor: string = styles && styles.textColor ? styles.textColor : theme.color.blackText;

  const prefixTypographyVariant: ResponsiveTypographyVariant =
    variant === PriceValueVariant.small
      ? ResponsiveTypographyVariant.nano
      : ResponsiveTypographyVariant.micro;
  const leadRateTypographyVariant: ResponsiveTypographyVariant =
    variant === PriceValueVariant.small
      ? ResponsiveTypographyVariant.t5SansBold
      : ResponsiveTypographyVariant.t4SansBold;
  const rackRateTypographyVariant: ResponsiveTypographyVariant =
    variant === PriceValueVariant.small
      ? ResponsiveTypographyVariant.nano
      : ResponsiveTypographyVariant.micro;
  const suffixTypographyVariant: ResponsiveTypographyVariant =
    variant === PriceValueVariant.small
      ? ResponsiveTypographyVariant.nano
      : ResponsiveTypographyVariant.micro;

  return (
    <CostContainer alignItems="baseline" className="costViewContainer">
      <CostWrapper>
        <PaddedTypography variant={prefixTypographyVariant} color={textColor}>
          {translatedPrefix}
        </PaddedTypography>
        <PaddedTypography
          variant={leadRateTypographyVariant}
          color={priceColor}
          dataAttributes={{ testid: 'leadRate' }}
        >
          {leadRate}
        </PaddedTypography>
      </CostWrapper>
      <CostWrapper>
        {!hideRackRate && (
          <PaddedTypography variant={rackRateTypographyVariant} color={theme.color.grey50}>
            <LineThrough>{rackRate}</LineThrough>
          </PaddedTypography>
        )}
        <ResponsiveTypography variant={suffixTypographyVariant} color={textColor}>
          {translatedSuffix}
        </ResponsiveTypography>
      </CostWrapper>
    </CostContainer>
  );
};

export default withTheme(CostView);
